import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, ClipboardList } from "lucide-react";

const EMPTY = { name: "", category: "consulenza", description: "", unit_price: "", vat_rate: 22, currency: "EUR" };

const CATEGORIES = ["trust", "societa_estere", "consulenza", "consulenza_trust", "consulenza_aziendale", "marchi_brevetti", "societa_innovative", "consulenza_trustee"];

export default function Services() {
  const { t, fmt } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [catFilter, setCatFilter] = useState("all");

  const catLabel = (c) => {
    const v = t(`cat.${c}`);
    return v === `cat.${c}` ? c : v;
  };
  const filtered = catFilter === "all" ? items : items.filter((s) => s.category === catFilter);

  const load = useCallback(() => {
    api.get("/services").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(t("common.name"));
      return;
    }
    try {
      await api.post("/services", { ...form, unit_price: parseFloat(form.unit_price) || 0, vat_rate: parseFloat(form.vat_rate) || 0 });
      toast.success(t("msg.saved"));
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/services/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const seedCatalog = async () => {
    try {
      const r = await api.post("/services/seed-trust-catalog");
      toast.success(r.data.created ? `${r.data.created} ${t("svc.seeded")}` : t("svc.alreadySeeded"));
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <ClipboardList className="h-7 w-7 text-primary" />{t("nav.services")}
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seedCatalog} data-testid="seed-trust-catalog-button">{t("svc.seedTrust")}</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-service-button"><Plus className="h-4 w-4 mr-2" />{t("svc.new")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("svc.new")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>{t("common.name")}</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="service-name-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("svc.category")}</Label>
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="service-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{t(`cat.${c}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("svc.price")}</Label>
                  <Input type="number" step="0.01" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} data-testid="service-price-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("inv.vatRate")}</Label>
                  <Input type="number" step="0.01" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} data-testid="service-vat-input" /></div>
                <div><Label>{t("svc.currency")}</Label>
                  <Input value={form.currency} onChange={(e) => set("currency", e.target.value)} maxLength={3} data-testid="service-currency-input" /></div>
              </div>
              <div><Label>{t("common.description")}</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="service-description-input" /></div>
              <Button className="w-full" onClick={submit} data-testid="service-submit-button">{t("common.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Label className="text-sm text-muted-foreground">{t("svc.category")}</Label>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[280px]" data-testid="category-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="category-filter-all">{t("common.all")}</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`category-filter-${c}`}>{t(`cat.${c}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.name")}</TableHead>
                  <TableHead>{t("svc.category")}</TableHead>
                  <TableHead>{t("common.description")}</TableHead>
                  <TableHead className="text-right">{t("svc.price")}</TableHead>
                  <TableHead className="text-right">{t("common.vat")} %</TableHead>
                  <TableHead>{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} data-testid={`service-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{s.category ? <Badge variant="secondary">{catLabel(s.category)}</Badge> : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{s.description || "—"}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmt(s.unit_price, s.currency)}</TableCell>
                    <TableCell className="text-right font-mono">{s.vat_rate}%</TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" data-testid={`delete-service-${s.id}`} onClick={() => remove(s.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
